/**
 * CheckoutPage — Unified cart-based checkout flow.
 *
 * Handles all purchase types: new subscriptions, upgrades, downgrades,
 * and free plans. Supports multiple items (multi-product).
 *
 * Steps:
 *   0. Order Review — cart items, billing address, tax, proration, totals
 *   1. Confirm & Pay — read-only summary, card input (if paid), confirm button
 *   2. Processing — per-item execution progress
 *   3. Complete — success with product links
 *
 * URL backward compat: /checkout?plan={planId}&product={productId}
 * constructs a single-item cart from URL params if cart is empty.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Alert, CircularProgress,
  Paper, Stepper, Step, StepLabel, TextField, Divider, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import billingApi from '../services/billingApi';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import BillingAddressForm from '../components/checkout/BillingAddressForm';
import OrderLineItem from '../components/checkout/OrderLineItem';
import OrderSummarySkeleton from '../components/OrderSummarySkeleton';
import OrderSummary from '../components/checkout/OrderSummary';

import { services, productLabelToServiceKey } from '../proxy-config';
import { classifyInstancePoll, seedOutcomes, allItemsResolved, finalizeOutcomes, anyFailed, anyManualReview } from '../utils/checkoutOutcome';
import { generateAttemptToken } from '../utils/attemptToken';

function getProductUrl(productId) {
  const key = productLabelToServiceKey[productId];
  return key ? services[key]?.frontend : null;
}

const STEPS = ['Review Order', 'Confirm & Pay', 'Processing', 'Complete'];

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const { items, removeItem, clearCart, billingAddress, updateBillingAddress, addItem } = useCart();

  // 999.5282: minted ONCE per mount (lazy initializer — not re-run on
  // re-render), resent unchanged on every retry of this attempt so a
  // double-click or a network retry of the same in-flight submit collides
  // server-side. A fresh mount (a genuinely new visit) mints a new one, and
  // so does a terminally FAILED order — see the anyFailed branch in
  // handleConfirm for why that one is required, and why manual_review is
  // deliberately excluded. A page REFRESH remounts, so it is NOT deduped.
  const [attemptToken, setAttemptToken] = useState(generateAttemptToken);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState([]);
  // 999.4741/999.4758/999.4760 BLOCK 6: which cart items ended up
  // manual_review — a Stripe action may already have executed for these and
  // is unverified. Persisted (not just a local var in handleConfirm) so the
  // Complete step can render an honest "parked for review" outcome instead
  // of a plain success, and so it never shows a per-item "Failed" chip for
  // an item that hasn't actually failed.
  const [finalOutcomesState, setFinalOutcomesState] = useState({});

  const previewDebounceRef = useRef(null);
  const addressAutoFilled = useRef(false);

  // Backward compat: construct cart from URL params if cart is empty
  useEffect(() => {
    const planId = searchParams.get('plan');
    const productId = searchParams.get('product');

    if (items.length === 0 && planId) {
      (async () => {
        try {
          const params = productId ? `?product=${productId}` : '';
          const data = await billingApi.get(`/api/plans/actions${params}`);
          const plan = data.plans?.find(p => p.id === planId || p.planId === planId);
          if (plan && plan.action) {
            addItem({
              id: `${plan.id}_${plan.action.type}`,
              planId: plan.id,
              productId: plan.productId,
              productName: plan.product_name,
              planName: plan.name,
              actionType: plan.action.type,
              priceCents: plan.price_cents,
              intervalType: plan.interval_type,
              subscriptionId: plan.action.payload?.input_data?.subscription_id || null,
              workflowId: null,
              workflowPayload: plan.action.payload,
            });
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch preview when items or billing address change
  useEffect(() => {
    if (items.length === 0) { setPreview(null); return; }

    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      try {
        const data = await billingApi.post('/api/checkout/preview', {
          items: items.map(i => ({
            planId: i.planId,
            actionType: i.actionType,
            subscriptionId: i.subscriptionId,
            timing: i.timing,
            proration: i.proration,
            description: i.description,
          })),
          billingAddress: billingAddress,
        });
        setPreview(data);
        // Pre-fill billing address from customer on first load only
        if (!addressAutoFilled.current && !billingAddress && data.customer?.billing_state) {
          addressAutoFilled.current = true;
          updateBillingAddress({
            line1: data.customer.billing_address_line1 || '',
            city: data.customer.billing_city || '',
            state: data.customer.billing_state || '',
            zip: data.customer.billing_zip || '',
            country: data.customer.billing_country || 'US',
          });
        }
      } catch (err) {
        console.warn('Preview failed:', err.message);
      }
    }, 500);

    return () => { if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current); };
  }, [items, billingAddress]);

  // Execute checkout
  async function handleConfirm() {
    setProcessing(true);
    setError('');
    setActiveStep(2);

    try {
      const data = await billingApi.post('/api/checkout/execute', {
        items: items.map(i => ({
          planId: i.planId,
          actionType: i.actionType,
          subscriptionId: i.subscriptionId,
        })),
        billingAddress: billingAddress,
        attemptToken,
      });

      const results = data.results || [];
      setResults(results);

      // Seed outcomes from synchronous results (e.g. free-plan activation,
      // or a synchronous rejection like "plan not found") before any polling.
      let outcomes = seedOutcomes(results);

      // 999.5282 round-3 review (harrison + law, independently): the re-mint gate
      // below needs to know an item started NO workflow — so no Stripe Checkout
      // Session can exist behind it. An earlier draft inferred that from "the
      // failure was in the pre-poll seed", which is WRONG in both directions:
      //   - a workflow can start and die INSIDE collect_payment, after Stripe
      //     created the session, and still report failed synchronously; and
      //   - on a repeat submit the server's own reuse path returns the existing
      //     failed instance, so a poll-INFERRED failure arrives seeded next time.
      // The distinguishing field is the instance id, which the outcome map drops.
      // `conflict` is the server's explicit "something else already owns this
      // attempt's workflow" tag (see executeCheckout's per-item catch) — those
      // carry no workflowInstanceId either, but they are the opposite of proof.
      const provenNoSession = new Set(
        results
          .filter((r) => r.status === 'failed' && !r.workflowInstanceId && !r.conflict)
          .map((r) => r.planId)
      );

      // Check if any workflow is paused for payment (needs Stripe checkout redirect)
      for (const result of results) {
        if (result.workflowInstanceId && result.status === 'running') {
          // Poll the workflow instance to check for paused_for_payment
          let attempts = 0;
          while (attempts < 10) {
            await new Promise(r => setTimeout(r, 1000));
            try {
              const instanceData = await billingApi.get(
                `/api/workflows/instances/${result.workflowInstanceId}`
              );
              const outcome = classifyInstancePoll(instanceData.instance);
              if (outcome && outcome.redirectUrl) {
                // Redirect to Stripe checkout page
                window.location.href = outcome.redirectUrl;
                return;
              }
              // 999.4741/999.4758/999.4760 BLOCK 6: 'manual_review' must be
              // recorded as its OWN outcome and stop this inner poll (break),
              // same as completed/failed — otherwise this loop exhausts its
              // 10 attempts, the item falls through unresolved into the
              // subscription-confirmation loop below, and (since the DB
              // subscription row for this workflow shape is created AFTER
              // the Stripe step that's stuck) finalizeOutcomes eventually
              // mislabels it 'failed' — a retry CTA on a provider action
              // that may already have executed.
              if (outcome === 'completed' || outcome === 'failed' || outcome === 'manual_review') {
                outcomes = { ...outcomes, [result.planId]: outcome };
                break;
              }
            } catch {}
            attempts++;
          }
        }
      }

      // Poll for subscription confirmation (for workflows that completed without payment pause)
      let attempts = 0;
      let lastKnownSubs = [];
      while (attempts < 15) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const subData = await billingApi.get('/api/subscriptions');
          lastKnownSubs = subData.subscriptions || [];
          if (allItemsResolved(items, outcomes, lastKnownSubs)) break;
        } catch {}
        attempts++;
      }

      // Resolve any remaining unresolved item (poll-exhaustion) against the
      // last-known subscription state — an item we can't confirm active is
      // NOT a success, regardless of how the loop above ended.
      const finalOutcomes = finalizeOutcomes(items, outcomes, lastKnownSubs);
      setFinalOutcomesState(finalOutcomes);

      if (refreshSession) await refreshSession();

      if (anyFailed(finalOutcomes)) {
        // 999.5282 review (harrison finding 1): this branch re-enables the
        // Confirm Order button, so it IS the retry CTA. Without re-minting,
        // that retry derives the same instance id, the server's reuse
        // pre-check hands back the SAME terminally-failed instance, and the
        // button is a permanent no-op until the customer reloads the page.
        // Re-minting here is exactly what that reload would do (a fresh mount
        // mints a new token) — so it costs no double-charge protection.
        //
        // TWO guards, from the 999.5282 round-2 review. Re-minting is only safe
        // when NOTHING in this cart can still hold a live, payable Stripe
        // Checkout Session — otherwise the retry derives a fresh instance id,
        // misses the server's reuse pre-check, and opens a SECOND session on an
        // item whose first one is still payable (Stripe keeps them live ~24h and
        // nothing in this repo expires them).
        //
        //  (a) !anyManualReview — harrison's BLOCK. A MIXED cart can carry a
        //      'failed' item alongside a 'manual_review' one; finalizeOutcomes
        //      preserves manual_review entries and anyFailed is evaluated FIRST,
        //      so the else-if below never sees that cart. Without this, the
        //      unverified item gets re-minted too.
        //  (b) every 'failed' entry STARTED NO WORKFLOW — see provenNoSession
        //      above. finalizeOutcomes synthesizes 'failed' for items that merely
        //      went unconfirmed before the polls ran out, and classifyInstancePoll
        //      maps 'canceled' to 'failed'; in the enroll flow collect_payment
        //      runs BEFORE create_db_sub, so a slow Stripe leg looks exactly like
        //      a failure while its session is live and unpaid. Only an item with
        //      no workflowInstanceId and no conflict tag is proof no session
        //      exists. (Round 2 keyed this on "was it in the pre-poll seed",
        //      which round 3 showed is not the same question.)
        //
        // Cost when a guard blocks: the retry CTA reverts to the pre-diff
        // no-op-until-reload behavior. That is the safe direction on a money path.
        const failedWithoutSession = Object.entries(finalOutcomes)
          .every(([planId, outcome]) => outcome !== 'failed' || provenNoSession.has(planId));
        if (!anyManualReview(finalOutcomes) && failedWithoutSession) {
          setAttemptToken(generateAttemptToken());
        }
        setError('One or more items in your order could not be completed. Please check your payment method and try again, or contact support.');
        setActiveStep(1);
      } else if (anyManualReview(finalOutcomes)) {
        // 999.4741/999.4758/999.4760 BLOCK 6: at least one item's provider
        // action is unverified — neither a confirmed success nor a routine
        // failure. Do NOT show the retry-suggesting error path above (that
        // is the exact customer-reachable path toward a second charge), and
        // do NOT show a plain success either. Land on the Complete step,
        // which renders a distinct, non-committal message with no retry CTA
        // for this case (see the activeStep === 3 render below).
        setActiveStep(3);
      } else {
        setActiveStep(3);
      }
    } catch (err) {
      setError(err.message);
      setActiveStep(1);
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    // Static page chrome (Back button + Stepper) renders during loading too,
    // so only the Paper contents swap when the order arrives — no layout jump
    // (standard rule 1).
    return (
      <Container maxWidth="sm" sx={{ py: 4 }} aria-busy="true">
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/plans')} sx={{ mb: 2 }}>
          Back to Plans
        </Button>
        <Stepper activeStep={0} sx={{ mb: 4 }} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <OrderSummarySkeleton />
        <Box component="span" role="status" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          Loading your order…
        </Box>
      </Container>
    );
  }

  if (items.length === 0 && !loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <AddShoppingCartIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" sx={{ mb: 2 }}>Your cart is empty</Typography>
        <Button variant="contained" color="secondary" onClick={() => navigate('/plans')}>
          Browse Plans
        </Button>
      </Container>
    );
  }

  const hasPaidItems = items.some(i =>
    i.priceCents > 0 && i.actionType !== 'activate_free'
  );
  const allFree = !hasPaidItems;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/plans')} sx={{ mb: 2 }}>
        Back to Plans
      </Button>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ─── Step 0: Order Review ─── */}
      {activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Order Review
          </Typography>

          {/* Line items */}
          {items.map((item, i) => (
            <OrderLineItem
              key={item.id}
              item={item}
              previewItem={preview?.items?.[i]}
              onRemove={items.length > 1 ? () => removeItem(item.id) : undefined}
            />
          ))}

          {/* Totals */}
          {preview?.totals && <OrderSummary totals={preview.totals} />}

          {/* Billing Address — always collect for paid plans (needed for tax) */}
          {items.some(i => i.priceCents > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <BillingAddressForm
                address={billingAddress}
                onChange={updateBillingAddress}
              />
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {items.length >= 1 && (
              <Button variant="outlined" onClick={() => navigate('/plans')} sx={{ flex: 1 }}>
                {items.length === 1 ? 'Change Plan' : 'Add More'}
              </Button>
            )}
            <Button
              variant="contained" color="secondary" size="large"
              onClick={() => setActiveStep(1)}
              sx={{ flex: 2, py: 1.5 }}
            >
              Continue
            </Button>
          </Box>
        </Paper>
      )}

      {/* ─── Step 1: Confirm & Pay ─── */}
      {activeStep === 1 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Confirm Order
          </Typography>

          {/* Compact order summary */}
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
            {items.map((item, i) => {
              const pi = preview?.items?.[i];
              return (
                <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {item.productName} — {item.planName}
                    {' '}<Chip label={item.actionType} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${((pi?.line_total_cents ?? item.priceCents) / 100).toFixed(2)}
                  </Typography>
                </Box>
              );
            })}
            {preview?.totals && <OrderSummary totals={preview.totals} compact />}
          </Box>

          {/* Payment note (if paid items) */}
          {hasPaidItems && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <LockIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                You'll be redirected to our secure payment page after confirming.
              </Typography>
            </Box>
          )}

          {allFree && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No payment required — your plan activates immediately.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)} sx={{ flex: 1 }}>
              Back
            </Button>
            <Button
              variant="contained" color="secondary" size="large"
              onClick={handleConfirm}
              disabled={processing}
              sx={{ flex: 2, py: 1.5 }}
            >
              {processing ? <CircularProgress size={24} color="inherit" /> : 'Confirm Order'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ─── Step 2: Processing ─── */}
      {activeStep === 2 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">Processing your order...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>This may take a moment.</Typography>
          <Box sx={{ textAlign: 'left', mt: 2 }}>
            {items.map(item => {
              const result = results.find(r => r.planId === item.planId);
              const status = result?.status || 'running';
              return (
                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {status === 'completed' && <CheckCircleIcon color="success" fontSize="small" />}
                  {status === 'failed' && <Chip label="Failed" size="small" color="error" />}
                  {status === 'running' && <CircularProgress size={16} />}
                  <Typography variant="body2">{item.productName} — {item.planName}</Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* ─── Step 3: Complete ─── */}
      {activeStep === 3 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          {anyManualReview(finalOutcomesState) ? (
            <>
              <Typography variant="h4" sx={{ mb: 1 }}>
                We're reviewing part of your order
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We need to verify one or more items with our payment provider before confirming them.
                No action is needed from you right now — please don't submit this order again.
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ mb: 1 }}>
                You're All Set!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {results.some(r => r.status === 'failed')
                  ? 'Some items could not be processed. Check your subscriptions for details.'
                  : 'Your subscriptions are now active.'}
              </Typography>
            </>
          )}

          {/* Per-item results — 999.4741/999.4758/999.4760 BLOCK 6: read the
              AUTHORITATIVE final outcome (finalOutcomesState), not the raw
              synchronous `results` snapshot, so a manual_review item never
              renders the same red "Failed" chip as a genuinely failed one. */}
          <Box sx={{ mb: 3 }}>
            {items.map(item => {
              const outcome = finalOutcomesState[item.planId];
              const result = results.find(r => r.planId === item.planId);
              const ok = outcome === 'completed';
              const parked = outcome === 'manual_review';
              return (
                <Box key={item.id} sx={{ mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {ok && <CheckCircleIcon color="success" fontSize="small" />}
                    {parked && <Chip label="Reviewing" size="small" color="warning" />}
                    {!ok && !parked && <Chip label="Failed" size="small" color="error" />}
                    <Typography variant="body2">{item.productName} — {item.planName}</Typography>
                  </Box>
                  {!ok && !parked && result?.error && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center' }}>
                      {result.error}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Product links */}
            {[...new Set(items.map(i => i.productId))].map(pid => {
              const url = getProductUrl(pid);
              if (!url) return null;
              const item = items.find(i => i.productId === pid);
              return (
                <Button key={pid} variant="contained" color="secondary" href={url}>
                  Go to {item.productName}
                </Button>
              );
            })}
            <Button variant="outlined" onClick={() => { clearCart(); navigate('/subscriptions'); }}>
              View Subscriptions
            </Button>
          </Box>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
        Managed by Raike & Sons Billing.
      </Typography>
    </Container>
  );
}
